import { GabcDetermination } from '../structures/GabcDetermination';
import { GregorioGlyphType } from '../enums/GregorioGlyphType';
import { GregorioLiquescentia } from '../enums/GregorioLiquescentia';
import { GregorioNoteElement } from '../structures/GregorioNoteElement';
import { GregorioShape } from '../enums/GregorioShape';

export function is_normal_punctum(note: GregorioNoteElement): boolean {
  return note.shape === GregorioShape.PUNCTUM && note.liquescentia !== GregorioLiquescentia.INITIO_DEBILIS;
}

export function is_punctum_inclinatum(shape: GregorioShape, determined_only: boolean): boolean {
  switch (shape) {
    case GregorioShape.PUNCTUM_INCLINATUM_ASCENDENS:
    case GregorioShape.PUNCTUM_INCLINATUM_STANS:
    case GregorioShape.PUNCTUM_INCLINATUM_DESCENDENS:
      return true;
    case GregorioShape.PUNCTUM_INCLINATUM_UNDETERMINED:
      return !determined_only;
    default:
      return false;
  }
}

export function add_note_to_a_glyph(
  current_glyph_type: GregorioGlyphType,
  current_pitch: number,
  last_pitch: number,
  shape: GregorioShape,
  liquescentia: GregorioLiquescentia,
  current_glyph_first_note: GregorioNoteElement) {
  /* next glyph type is the type of the glyph that will be returned (the
  * new type of the glyph with the note added to it, or the type of the
  * new glyph with the note alone. */
  let next_glyph_type = GregorioGlyphType.UNDETERMINED;
  let end_of_glyph = GabcDetermination.NO_END;
  // let result_glyph_type = current_glyph_type;

  /* here we separate notes that would be logically in the same glyph
  * but that are too far to be so */
  // if (last_pitch) {
  //   if (current_pitch - last_pitch > MAX_AMBITUS
  //     || current_pitch - last_pitch < -MAX_AMBITUS) {
  //     result_glyph_type = GregorioGlyphType.UNDETERMINED;
  //   }
  // }

  switch (shape) {
    case GregorioShape.LINEA_PUNCTUM:
    case GregorioShape.LINEA:
      next_glyph_type = GregorioGlyphType.PUNCTUM;
      end_of_glyph = GabcDetermination.END_OF_BOTH;
      break;
    case GregorioShape.FLAT:
    case GregorioShape.FLAT_PAREN:
    case GregorioShape.SHARP:
    case GregorioShape.SHARP_PAREN:
    case GregorioShape.NATURAL:
    case GregorioShape.NATURAL_PAREN:
      next_glyph_type = GregorioGlyphType.ALTERATION;
      end_of_glyph = GabcDetermination.END_OF_BOTH;
      break;
    case GregorioShape.PUNCTUM:
      /*
       * we determine here the shape of the thing if it is made of puncta
       */
      if (current_pitch == last_pitch) {
        next_glyph_type = GregorioGlyphType.PUNCTUM;
        end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
        break;
      }
      switch (current_glyph_type) {
        case GregorioGlyphType.PUNCTUM:
          if (current_pitch > last_pitch) {
            next_glyph_type = GregorioGlyphType.PODATUS;
          } else {
            next_glyph_type = GregorioGlyphType.FLEXA;
          }
          break;
        case GregorioGlyphType.PODATUS:
          if (current_pitch > last_pitch) {
            if (is_normal_punctum(current_glyph_first_note)) {
              next_glyph_type = GregorioGlyphType.SCANDICUS;
              end_of_glyph = GabcDetermination.END_OF_CURRENT;
            } else {
              next_glyph_type = GregorioGlyphType.PUNCTUM;
              end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
            }
          } else {
            next_glyph_type = GregorioGlyphType.TORCULUS;
          }
          break;
        case GregorioGlyphType.PES_QUADRATUM_FIRST_PART:
          if (current_pitch > last_pitch) {
            next_glyph_type = GregorioGlyphType.PES_QUADRATUM;
            end_of_glyph = GabcDetermination.END_OF_CURRENT;
          } else {
            next_glyph_type = GregorioGlyphType.FLEXA;
          }
          break;
        case GregorioGlyphType.PES_QUILISMA_QUADRATUM_FIRST_PART:
          if (current_pitch > last_pitch) {
            next_glyph_type = GregorioGlyphType.PES_QUADRATUM;
            end_of_glyph = GabcDetermination.END_OF_CURRENT;
          } else {
            next_glyph_type = GregorioGlyphType.PUNCTUM;
            end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
          }
          break;
        case GregorioGlyphType.PES_ASCENDENS_ORISCUS:
        case GregorioGlyphType.PES_DESCENDENS_ORISCUS:
          if (current_pitch > last_pitch) {
            next_glyph_type = GregorioGlyphType.SALICUS;
          } else {
            next_glyph_type = GregorioGlyphType.PUNCTUM;
            end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
          }
          break;
        case GregorioGlyphType.SALICUS:
          if (current_pitch < last_pitch) {
            next_glyph_type = GregorioGlyphType.SALICUS_FLEXUS;
            end_of_glyph = GabcDetermination.END_OF_CURRENT;
          } else {
            next_glyph_type = GregorioGlyphType.PUNCTUM;
            end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
          }
          break;
        case GregorioGlyphType.FLEXA:
          if (current_pitch > last_pitch) {
            if (is_normal_punctum(current_glyph_first_note)) {
              next_glyph_type = GregorioGlyphType.PORRECTUS;
            } else {
              next_glyph_type = GregorioGlyphType.PUNCTUM;
              end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
            }
          } else {
            if (liquescentia & GregorioLiquescentia.DEMINUTUS) {
              end_of_glyph = GabcDetermination.END_OF_CURRENT;
              next_glyph_type = GregorioGlyphType.ANCUS;
            } else {
              next_glyph_type = GregorioGlyphType.PUNCTUM;
              end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
            }
          }
          break;
        case GregorioGlyphType.TORCULUS:
          if (current_pitch > last_pitch) {
            next_glyph_type = GregorioGlyphType.TORCULUS_RESUPINUS;
          } else if (liquescentia == GregorioLiquescentia.DEMINUTUS) {
            end_of_glyph = GabcDetermination.END_OF_CURRENT;
            next_glyph_type = GregorioGlyphType.TORCULUS_LIQUESCENS;
          } else {
            next_glyph_type = GregorioGlyphType.PUNCTUM;
            end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
          }
          break;
        case GregorioGlyphType.TORCULUS_RESUPINUS:
          if (current_pitch > last_pitch) {
            next_glyph_type = GregorioGlyphType.PUNCTUM;
            end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
          } else {
            end_of_glyph = GabcDetermination.END_OF_CURRENT;
            next_glyph_type = GregorioGlyphType.TORCULUS_RESUPINUS_FLEXUS;
          }
          break;
        case GregorioGlyphType.PORRECTUS:
          if (current_pitch > last_pitch) {
            next_glyph_type = GregorioGlyphType.PUNCTUM;
            end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
          } else {
            end_of_glyph = GabcDetermination.END_OF_CURRENT;
            next_glyph_type = GregorioGlyphType.PORRECTUS_FLEXUS;
          }
          break;
        default:
          next_glyph_type = GregorioGlyphType.PUNCTUM;
          end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
          break;
      }
      break;
    case GregorioShape.ORISCUS_UNDETERMINED:
    case GregorioShape.ORISCUS_ASCENDENS:
    case GregorioShape.ORISCUS_DESCENDENS:
    case GregorioShape.ORISCUS_DEMINUTUS:
    case GregorioShape.QUILISMA:
      next_glyph_type = GregorioGlyphType.PUNCTUM;
      end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
      break;
    case GregorioShape.VIRGA:
      if (current_glyph_type == GregorioGlyphType.VIRGA && last_pitch == current_pitch) {
        next_glyph_type = GregorioGlyphType.BIVIRGA;
      } else {
        if (current_glyph_type == GregorioGlyphType.BIVIRGA && last_pitch == current_pitch) {
          next_glyph_type = GregorioGlyphType.TRIVIRGA;
        } else {
          end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
          next_glyph_type = GregorioGlyphType.VIRGA;
        }
      }
      break;
    case GregorioShape.VIRGA_REVERSA:
      end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
      next_glyph_type = GregorioGlyphType.VIRGA_REVERSA;
      break;
    case GregorioShape.BIVIRGA:
      if (current_glyph_type == GregorioGlyphType.VIRGA && last_pitch == current_pitch) {
        end_of_glyph = GabcDetermination.END_OF_CURRENT;
        next_glyph_type = GregorioGlyphType.TRIVIRGA;
      } else {
        end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
        next_glyph_type = GregorioGlyphType.BIVIRGA;
      }
      break;
    case GregorioShape.TRIVIRGA:
      end_of_glyph = GabcDetermination.END_OF_BOTH;
      next_glyph_type = GregorioGlyphType.TRIVIRGA;
      break;
    case GregorioShape.QUADRATUM:
      end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
      next_glyph_type = GregorioGlyphType.PES_QUADRATUM_FIRST_PART;
      break;
    case GregorioShape.QUILISMA_QUADRATUM:
      end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
      next_glyph_type = GregorioGlyphType.PES_QUILISMA_QUADRATUM_FIRST_PART;
      break;
    case GregorioShape.ORISCUS_SCAPUS_UNDETERMINED:
    case GregorioShape.ORISCUS_SCAPUS_ASCENDENS:
      if (current_glyph_type == GregorioGlyphType.PUNCTUM && last_pitch < current_pitch) {
        next_glyph_type = GregorioGlyphType.PES_ASCENDENS_ORISCUS;
        /* Therefore GregorioGlyphType.PES_ASCENDENS_ORISCUS might be fronting an
         * undetermined oriscus scapus.  This will be resolved during
         * oriscus orientation determination */
      } else {
        next_glyph_type = GregorioGlyphType.PUNCTUM;
        end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
      }
      break;
    case GregorioShape.ORISCUS_SCAPUS_DESCENDENS:
      if (current_glyph_type == GregorioGlyphType.PUNCTUM && last_pitch < current_pitch) {
        next_glyph_type = GregorioGlyphType.PES_DESCENDENS_ORISCUS;
      } else {
        next_glyph_type = GregorioGlyphType.PUNCTUM;
        end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
      }
      break;
    case GregorioShape.PUNCTUM_INCLINATUM_UNDETERMINED:
    case GregorioShape.PUNCTUM_INCLINATUM_ASCENDENS:
    case GregorioShape.PUNCTUM_INCLINATUM_STANS:
    case GregorioShape.PUNCTUM_INCLINATUM_DESCENDENS:
      /*
       * Warning : this part of the code is specific to the
       * declarations of the header file
       */
      if (current_glyph_type > GregorioGlyphType.PUNCTA_INCLINATA) {
        /*
         * meaning that the previous glyph is not a combination of puncta
         * inclinata, see header file for details
         */
        end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
        next_glyph_type = GregorioGlyphType.PUNCTUM_INCLINATUM;
        break;
      }
      if (current_pitch === last_pitch) {
        end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
        next_glyph_type = GregorioGlyphType.PUNCTUM_INCLINATUM;
        break;
      }
      if (is_punctum_inclinatum(shape, true)) {
        /* change of orientation ends the glyph */
        end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
        next_glyph_type = GregorioGlyphType.PUNCTUM_INCLINATUM;
        break;
      }
      switch (current_glyph_type) {
        case GregorioGlyphType.PUNCTUM_INCLINATUM:
          if (last_pitch < current_pitch) {
            next_glyph_type = GregorioGlyphType._2_PUNCTA_INCLINATA_ASCENDENS;
          } else {
            next_glyph_type = GregorioGlyphType._2_PUNCTA_INCLINATA_DESCENDENS;
          }
          break;
        case GregorioGlyphType._2_PUNCTA_INCLINATA_ASCENDENS:
          if (last_pitch < current_pitch) {
            next_glyph_type = GregorioGlyphType._3_PUNCTA_INCLINATA_ASCENDENS;
          } else {
            next_glyph_type = GregorioGlyphType.PUNCTA_INCLINATA;
          }
          break;
        case GregorioGlyphType._3_PUNCTA_INCLINATA_ASCENDENS:
          if (last_pitch < current_pitch) {
            next_glyph_type = GregorioGlyphType._4_PUNCTA_INCLINATA_ASCENDENS;
          } else {
            next_glyph_type = GregorioGlyphType.PUNCTA_INCLINATA;
          }
          break;
        case GregorioGlyphType._4_PUNCTA_INCLINATA_ASCENDENS:
          if (last_pitch < current_pitch) {
            next_glyph_type = GregorioGlyphType._5_PUNCTA_INCLINATA_ASCENDENS;
          } else {
            next_glyph_type = GregorioGlyphType.PUNCTA_INCLINATA;
          }
          break;
        case GregorioGlyphType._2_PUNCTA_INCLINATA_DESCENDENS:
          if (last_pitch < current_pitch) {
            next_glyph_type = GregorioGlyphType.PUNCTA_INCLINATA;
          } else {
            next_glyph_type = GregorioGlyphType._3_PUNCTA_INCLINATA_DESCENDENS;
          }
          break;
        case GregorioGlyphType._3_PUNCTA_INCLINATA_DESCENDENS:
          if (last_pitch < current_pitch) {
            next_glyph_type = GregorioGlyphType.PUNCTA_INCLINATA;
          } else {
            next_glyph_type = GregorioGlyphType._4_PUNCTA_INCLINATA_DESCENDENS;
          }
          break;
        case GregorioGlyphType._4_PUNCTA_INCLINATA_DESCENDENS:
          if (last_pitch < current_pitch) {
            next_glyph_type = GregorioGlyphType.PUNCTA_INCLINATA;
          } else {
            next_glyph_type = GregorioGlyphType._5_PUNCTA_INCLINATA_DESCENDENS;
          }
          break;
        default:
          next_glyph_type = GregorioGlyphType.PUNCTA_INCLINATA;
          break;
      }
      break;
    case GregorioShape.STROPHA:
      if (last_pitch != current_pitch) {
        end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
        next_glyph_type = GregorioGlyphType.STROPHA;
        break;
      }
      switch (current_glyph_type) {
        case GregorioGlyphType.STROPHA:
          next_glyph_type = GregorioGlyphType.DISTROPHA;
          break;
        case GregorioGlyphType.DISTROPHA:
          end_of_glyph = GabcDetermination.END_OF_CURRENT;
          next_glyph_type = GregorioGlyphType.TRISTROPHA;
          break;
        default:
          end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
          next_glyph_type = GregorioGlyphType.STROPHA;
          break;
      }
      break;
    case GregorioShape.DISTROPHA:
      if (last_pitch === current_pitch && current_glyph_type === GregorioGlyphType.STROPHA) {
        end_of_glyph = GabcDetermination.END_OF_CURRENT;
        next_glyph_type = GregorioGlyphType.TRISTROPHA;
      } else {
        end_of_glyph = GabcDetermination.END_OF_PREVIOUS;
        next_glyph_type = GregorioGlyphType.DISTROPHA;
      }
      break;
    case GregorioShape.TRISTROPHA:
      end_of_glyph = GabcDetermination.END_OF_BOTH;
      next_glyph_type = GregorioGlyphType.TRISTROPHA;
      break;
    default:
      /* not reachable unless there's a programming error */
      /* LCOV_EXCL_START */
      throw new Error('unexpected shape: ' + (shape));
      // gregorio_fail2(add_note_to_a_glyph, "unexpected shape: %s",
      //   gregorio_shape_to_string(shape));
      break;
    /* LCOV_EXCL_STOP */
  }
  /* end of the main switch */

  if (current_glyph_type == GregorioGlyphType.UNDETERMINED) {
    /*
     * means that this is the first glyph or that the previous glyph has
     * already been added
     */
    if (end_of_glyph == GabcDetermination.END_OF_PREVIOUS) {
      end_of_glyph = GabcDetermination.NO_END;
    } else {
      if (end_of_glyph == GabcDetermination.END_OF_BOTH) {
        end_of_glyph = GabcDetermination.END_OF_CURRENT;
      }
    }
  }

  return {
    next_glyph_type,
    end_of_glyph,
  };
}
